import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { fetchData, fetchJson } from '../../api/fetchJson'
import { orgPath } from '../../config/orgId'
import { useProject } from '../projects/useProject'
import {
  Button,
  Card,
  Empty,
  ErrorBanner,
  Field,
  FormattedDate,
  Input,
  Loading,
  PageHeader,
  Tag,
} from '../../components/ui'
import { flagIfAuthError } from '../../components/ResetBanner'

function isoToYyyymmdd(iso: string): string {
  return iso.replace(/-/g, '')
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function isoDaysAgo(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return d.toISOString().slice(0, 10)
}

function formatYyyymmdd(yyyymmdd: number | undefined): string {
  if (!yyyymmdd) return '—'
  const s = String(yyyymmdd)
  if (s.length !== 8) return s
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`
}

type AttendanceReportRecord = {
  id: string
  user_id: string
  project_id: string
  check_type: 'checkin' | 'checkout'
  timestamp: number
  user_display_name: string
  user_role: string
  project_name: string
  is_suspicious: boolean | number
}

type AttendanceReportSummary = {
  user_id: string
  date: number
  total_hours: number | null
  user_display_name: string
  user_role: string
  first_checkin: number | null
  last_checkout: number | null
}

type AttendanceReportStats = {
  total_days: number
  total_hours: number
  average_hours_per_day: number
}

type AttendanceReport = {
  records: AttendanceReportRecord[]
  summaries: AttendanceReportSummary[]
  stats: AttendanceReportStats
  total: number
}

type AttendanceEntry = {
  id: string
  user_id: string
  user_name?: string
  project_id: string
  checkin_at?: number | null
  checkout_at?: number | null
  status?: string
}

type TodayAttendanceResponse = {
  checkins: AttendanceEntry[]
  summary: { projects_visited: string[] } | null
  current_status?: string | null
  current_project_id?: string | null
}

async function getCoords(): Promise<{ lat: number; lng: number } | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return null
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { timeout: 5000 },
    )
  })
}

export function AttendancePage() {
  const { projectId } = useParams<{ projectId: string }>()
  const project = useProject(projectId)
  const qc = useQueryClient()

  const today = useQuery<TodayAttendanceResponse>({
    queryKey: ['attendance-today', projectId],
    enabled: Boolean(projectId),
    queryFn: () => fetchData<TodayAttendanceResponse>(orgPath(`/attendance/today`)),
  })

  const projectCheckins = (today.data?.checkins ?? []).filter(
    (c) => !projectId || c.project_id === projectId,
  )

  const checkin = useMutation({
    mutationFn: async () => {
      const coords = (await getCoords()) ?? { lat: 0, lng: 0 }
      return fetchJson(orgPath('/attendance/checkin'), {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          latitude: coords.lat,
          longitude: coords.lng,
        }),
      })
    },
    onError: flagIfAuthError,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attendance-today', projectId] }),
  })

  const checkout = useMutation({
    mutationFn: async () => {
      const coords = (await getCoords()) ?? { lat: 0, lng: 0 }
      return fetchJson(orgPath('/attendance/checkout'), {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          latitude: coords.lat,
          longitude: coords.lng,
        }),
      })
    },
    onError: flagIfAuthError,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attendance-today', projectId] }),
  })

  if (today.isLoading) return <Loading />
  if (today.isError) return <ErrorBanner error={today.error} />

  return (
    <div className="space-y-6">
      <PageHeader
        title="Attendance"
        subtitle={
          project.data?.name
            ? `Today's check-ins for ${project.data.name}`
            : "Today's check-ins"
        }
        actions={
          <div className="flex gap-2">
            <Button onClick={() => checkin.mutate()} disabled={checkin.isPending}>
              {checkin.isPending ? 'Checking in…' : 'Check in'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => checkout.mutate()}
              disabled={checkout.isPending}
            >
              {checkout.isPending ? 'Checking out…' : 'Check out'}
            </Button>
          </div>
        }
      />

      {(checkin.isError || checkout.isError) && (
        <ErrorBanner error={(checkin.error ?? checkout.error) as unknown} />
      )}

      {projectCheckins.length === 0 ? (
        <Empty
          message="No check-ins for today yet."
          hint="Tap Check in to log your attendance. Allow geolocation when prompted."
        />
      ) : (
        <Card>
          <ul className="divide-y divide-surface-700 text-sm">
            {projectCheckins.map((e) => (
              <li key={e.id} className="py-3 flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium">{e.user_name ?? e.user_id.slice(0, 8)}</div>
                  <div className="text-xs text-surface-400">
                    In: <FormattedDate ts={e.checkin_at ?? null} />
                    {' • '}
                    Out: <FormattedDate ts={e.checkout_at ?? null} />
                  </div>
                </div>
                <Tag tone={e.checkout_at ? 'default' : 'success'}>
                  {e.checkout_at ? 'Closed' : 'Open'}
                </Tag>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <AttendanceRangeReport projectId={projectId} />
    </div>
  )
}

function AttendanceRangeReport({ projectId }: { projectId: string | undefined }) {
  const [from, setFrom] = useState(() => isoDaysAgo(7))
  const [to, setTo] = useState(() => todayIso())
  const q = useQuery<AttendanceReport>({
    queryKey: ['attendance-report', projectId, from, to],
    enabled: Boolean(projectId && from && to),
    queryFn: () => {
      const params = new URLSearchParams({
        from_date: isoToYyyymmdd(from),
        to_date: isoToYyyymmdd(to),
      })
      if (projectId) params.set('project_id', projectId)
      return fetchData<AttendanceReport>(
        orgPath(`/attendance/report?${params.toString()}`),
      )
    },
  })

  return (
    <Card>
      <h2 className="font-semibold mb-3">Attendance report</h2>
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <Field label="From">
          <Input
            type="date"
            value={from}
            max={to}
            onChange={(e) => setFrom(e.target.value)}
          />
        </Field>
        <Field label="To">
          <Input
            type="date"
            value={to}
            min={from}
            onChange={(e) => setTo(e.target.value)}
          />
        </Field>
      </div>
      {q.isLoading ? (
        <Loading />
      ) : q.isError ? (
        <ErrorBanner error={q.error} />
      ) : !q.data || q.data.summaries.length === 0 ? (
        <Empty message="No attendance for this range." />
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-xs text-surface-400">Total days</div>
              <div className="text-lg font-semibold">{q.data.stats.total_days}</div>
            </div>
            <div>
              <div className="text-xs text-surface-400">Total hours</div>
              <div className="text-lg font-semibold">
                {q.data.stats.total_hours.toFixed(1)}
              </div>
            </div>
            <div>
              <div className="text-xs text-surface-400">Avg / day</div>
              <div className="text-lg font-semibold">
                {q.data.stats.average_hours_per_day.toFixed(1)}
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-surface-400 border-b border-surface-700">
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">User</th>
                  <th className="py-2 pr-3">First in</th>
                  <th className="py-2 pr-3">Last out</th>
                  <th className="py-2">Hours</th>
                </tr>
              </thead>
              <tbody>
                {q.data.summaries.map((s, i) => (
                  <tr
                    key={`${s.user_id}-${s.date}-${i}`}
                    className="border-b border-surface-700/50"
                  >
                    <td className="py-2 pr-3">{formatYyyymmdd(s.date)}</td>
                    <td className="py-2 pr-3">{s.user_display_name}</td>
                    <td className="py-2 pr-3">
                      <FormattedDate ts={s.first_checkin ?? null} />
                    </td>
                    <td className="py-2 pr-3">
                      <FormattedDate ts={s.last_checkout ?? null} />
                    </td>
                    <td className="py-2">
                      {s.total_hours !== null ? s.total_hours.toFixed(1) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  )
}
